/**
 * Authentication Provider using MSAL React
 * Supports test mode for E2E testing via VITE_TEST_MODE environment variable
 */

import React, { ReactNode, useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '@/config/msal.config';
import { LoginPage } from './LoginPage';
import { useAppStore } from '@/store';
import { fetchMsGraphProfilePhoto, getInitials, getUserAvatarUrl } from '@/lib/avatar';

// Check if running in test mode (for E2E tests)
const isTestMode = import.meta.env.VITE_TEST_MODE === 'true';

// Initialize MSAL instance (only if not in test mode)
// eslint-disable-next-line react-refresh/only-export-components
export const msalInstance = isTestMode ? null : new PublicClientApplication(msalConfig);

// Initialize MSAL before any operations (only if not in test mode)
const msalInitPromise = isTestMode ? Promise.resolve() : msalInstance!.initialize().catch(console.error);

// Test user for E2E tests
const TEST_USER = {
  id: 'test-user-id',
  displayName: 'Test User',
  email: 'test@example.com',
  avatar: 'TU',
};

// Context for test mode auth
interface TestAuthContextType {
  isAuthenticated: boolean;
  user: typeof TEST_USER | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const TestAuthContext = createContext<TestAuthContextType | null>(null);

// Key to track if user explicitly logged out (prevents auto-SSO after logout)
// Using localStorage instead of sessionStorage to persist across redirects
const LOGOUT_FLAG_KEY = 'cherrypulse_logged_out';

/**
 * Loading screen shown while processing redirect
 */
function LoadingScreen(): React.ReactElement {
  return (
    <div className="min-h-screen bg-charcoal-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-lg flex items-center justify-center animate-pulse">
          <span className="text-3xl">🍒</span>
        </div>
        <p className="text-charcoal-400">Authentifizierung...</p>
      </div>
    </div>
  );
}

/**
 * Inner component that handles redirect state and SSO
 */
function AuthContent({ children }: { children: ReactNode }): React.ReactElement {
  const { inProgress, instance, accounts } = useMsal();
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const fetchDataFromBackend = useAppStore((state) => state.fetchDataFromBackend);
  const syncUserToBackend = useAppStore((state) => state.syncUserToBackend);
  const setUser = useAppStore((state) => state.setUser);
  const lastFetchedEnvRef = useRef<string | null>(null);
  const photoFetchedRef = useRef(false);
  const activeEnvironment = useAppStore((state) => state.activeEnvironment);

  // Fetch profile photo from MS Graph and sync to backend when authenticated
  useEffect(() => {
    const fetchProfilePhotoAndSync = async (): Promise<void> => {
      if (authState === 'authenticated' && accounts.length > 0 && !photoFetchedRef.current) {
        photoFetchedRef.current = true;
        const account = accounts[0];

        // Fetch MS Graph photo (may fail silently - that's ok)
        const photoDataUrl = await fetchMsGraphProfilePhoto(instance, account);

        // Update user in store with photo, unavatar fallback, or initials
        const displayName = account.name || account.username.split('@')[0];
        const avatarUrl = photoDataUrl || getUserAvatarUrl(account.username) || getInitials(displayName);
        setUser({
          id: account.localAccountId,
          displayName,
          email: account.username,
          avatar: avatarUrl,
        });

        // Sync user to backend with profile photo (so other users can see it)
        // Never trigger redirect for sync failures - this is a non-critical operation
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: false,
          });
          await syncUserToBackend(tokenResponse.idToken, photoDataUrl);
        } catch {
          // Silently fail - sync is non-critical and should never cause login loops
        }
      }
    };

    void fetchProfilePhotoAndSync();
  }, [authState, accounts, instance, setUser, syncUserToBackend]);

  // Fetch data from backend when authenticated or when environment changes
  useEffect(() => {
    // Skip if not authenticated or no accounts
    if (authState !== 'authenticated' || accounts.length === 0) {
      return;
    }

    // Skip if we already fetched for this environment
    if (lastFetchedEnvRef.current === activeEnvironment) {
      return;
    }

    const loadBackendData = async (): Promise<void> => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
          forceRefresh: false,
        });
        await fetchDataFromBackend(tokenResponse.idToken);
        // Only mark as fetched after successful fetch
        lastFetchedEnvRef.current = activeEnvironment;
      } catch (error) {
        // Silently fail - never trigger redirect from data fetch
        // This prevents login loops when token refresh fails in iframe
        console.warn('[Auth] Token acquisition failed for data fetch:', error);
        // Don't update lastFetchedEnvRef so it will retry on next render
      }
    };

    void loadBackendData();
  }, [authState, accounts, instance, fetchDataFromBackend, activeEnvironment]);

  useEffect(() => {
    let isMounted = true;

    const handleAuth = async (): Promise<void> => {
      try {
        // Wait for MSAL to initialize
        await msalInitPromise;

        // Handle any redirect response first
        // msalInstance is guaranteed to be non-null here because AuthContent only renders in non-test mode
        const response = await msalInstance!.handleRedirectPromise();
        if (response) {
          // Clear logout flag since user successfully logged in
          localStorage.removeItem(LOGOUT_FLAG_KEY);
          if (isMounted) setAuthState('authenticated');
          return;
        }

        // Check if already authenticated (from cache)
        const accounts = msalInstance!.getAllAccounts();
        if (accounts.length > 0) {
          // Clear logout flag since user is authenticated
          localStorage.removeItem(LOGOUT_FLAG_KEY);
          if (isMounted) setAuthState('authenticated');
          return;
        }

        // Check if user explicitly logged out - don't auto-SSO
        const userLoggedOut = localStorage.getItem(LOGOUT_FLAG_KEY);
        if (userLoggedOut) {
          if (isMounted) setAuthState('unauthenticated');
          return;
        }

        // Try silent SSO
        try {
          await msalInstance!.ssoSilent(loginRequest);
          if (isMounted) setAuthState('authenticated');
        } catch {
          // SSO failed - user needs to log in manually
          if (isMounted) setAuthState('unauthenticated');
        }
      } catch {
        if (isMounted) setAuthState('unauthenticated');
      }
    };

    void handleAuth();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - run only once on mount

  // Show loading while processing auth
  if (authState === 'loading' || inProgress !== 'none') {
    return <LoadingScreen />;
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
}

/**
 * Test Mode Auth Provider - bypasses MSAL for E2E testing
 * The store already has seed data in initial state, but persist middleware
 * might override it. In test mode, we ensure seed data is used.
 */
function TestAuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Auto-authenticated in test mode

  const login = useCallback((): Promise<void> => {
    setIsAuthenticated(true);
    return Promise.resolve();
  }, []);

  const logout = useCallback((): Promise<void> => {
    setIsAuthenticated(false);
    return Promise.resolve();
  }, []);

  return (
    <TestAuthContext.Provider value={{ isAuthenticated, user: TEST_USER, login, logout }}>
      {children}
    </TestAuthContext.Provider>
  );
}

/**
 * Main Auth Provider component
 * Uses TestAuthProvider in test mode, MsalProvider in production
 */
export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  // Use test provider in test mode
  if (isTestMode) {
    return <TestAuthProvider>{children}</TestAuthProvider>;
  }

  // Use MSAL provider in production
  return (
    <MsalProvider instance={msalInstance!}>
      <AuthContent>{children}</AuthContent>
    </MsalProvider>
  );
}

/**
 * Hook to get auth actions in test mode
 */
interface AuthHookReturn {
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  getAccount: () => AccountInfo | null;
  getUser: () => { id: string; displayName: string; email: string; avatar?: string } | null;
}

function useTestAuth(): AuthHookReturn {
  const context = useContext(TestAuthContext);
  if (!context) {
    throw new Error('useTestAuth must be used within TestAuthProvider');
  }

  return {
    isAuthenticated: context.isAuthenticated,
    login: context.login,
    logout: context.logout,
    getAccessToken: () => Promise.resolve('test-token'),
    getAccount: () => null,
    getUser: () => context.user,
  };
}

/**
 * Hook to get auth actions (MSAL mode)
 */
function useMsalAuth(): AuthHookReturn {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const login = useCallback(async (): Promise<void> => {
    // Clear logout flag since user is explicitly logging in
    localStorage.removeItem(LOGOUT_FLAG_KEY);
    // Use redirect instead of popup - works better with COOP headers
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Set flag to prevent auto-SSO after logout
      localStorage.setItem(LOGOUT_FLAG_KEY, 'true');
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch {
      // Clear flag if logout fails
      localStorage.removeItem(LOGOUT_FLAG_KEY);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts.length) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.idToken;
    } catch {
      // Fallback to redirect if silent fails
      try {
        await instance.acquireTokenRedirect(loginRequest);
        return null; // Will redirect, so we won't reach here
      } catch {
        return null;
      }
    }
  }, [instance, accounts]);

  const getAccount = useCallback((): AccountInfo | null => {
    return accounts.length > 0 ? accounts[0] : null;
  }, [accounts]);

  // Get user from store (includes avatar from MS Graph)
  const storeUser = useAppStore((state) => state.user);

  const getUser = useCallback((): { id: string; displayName: string; email: string; avatar?: string } | null => {
    // Return user from store if available (has MS Graph photo)
    if (storeUser) return storeUser;

    // Fallback to account info with initials
    const account = accounts[0];
    if (!account) return null;
    return {
      id: account.localAccountId,
      displayName: account.name || account.username.split('@')[0],
      email: account.username,
      avatar: (account.name || account.username)
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
    };
  }, [accounts, storeUser]);

  return {
    isAuthenticated,
    login,
    logout,
    getAccessToken,
    getAccount,
    getUser,
  };
}

/**
 * Hook to get auth actions
 * Automatically uses test mode or MSAL based on environment
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthHookReturn {
  // In test mode, we can't call useMsal (no MsalProvider)
  // So we need to conditionally use the appropriate hook
  // This works because the condition is static (determined at build time)
  if (isTestMode) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useTestAuth();
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMsalAuth();
}
